import { useState, useEffect } from 'react';
// Import debounce specifically from lodash
import { debounce } from 'lodash';

// Define the type for the return value
interface WindowSize {
  width: number;
  height: number;
}

/**
 * Custom hook to track window dimensions with debouncing using Lodash.
 * @param delay The debounce delay in milliseconds (default: 200).
 * @returns The debounced window dimensions ({ width, height }).
 */
export function useWindowResize(delay: number = 200): WindowSize {
  // Check if running in a browser environment (client-side)
  const isClient = typeof window !== 'undefined';

  // 1. Initialize state with the current window size or 0 for SSR
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: isClient ? window.innerWidth : 0,
    height: isClient ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (!isClient) {
      return;
    }

    // Define the handler function
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // 2. Create the debounced version of the handler using Lodash
    // We pass the handler function and the delay.
    const debouncedHandleResize = debounce(handleResize, delay);

    // Attach the event listener
    window.addEventListener('resize', debouncedHandleResize);

    // 3. Cleanup function: This is the most crucial part when using Lodash debounce
    return () => {
      // Remove the listener using the same debounced function reference
      window.removeEventListener('resize', debouncedHandleResize);

      // *** IMPORTANT: Cancel any pending debounced execution before unmount ***
      // Lodash's debounce returns a function with a .cancel() method,
      // which we must call to prevent the handler from running after the
      // component is unmounted.
      debouncedHandleResize.cancel();
    };
    // The effect re-runs if the delay changes
  }, [isClient, delay]);

  return windowSize;
}
